 
 
 
 
/* eslint-disable @typescript-eslint/no-this-alias */
/* eslint-disable @typescript-eslint/no-explicit-any */
 
/**
 * Performance utility functions for optimizing application behavior
 */

/**
 * Creates a debounced version of a function that delays invoking func until after
 * wait milliseconds have elapsed since the last time the debounced function was invoked.
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  options: { leading?: boolean; trailing?: boolean; maxWait?: number } = {}
): T & { cancel: () => void; flush: () => void } {
  let timeout: NodeJS.Timeout | null = null;
  let lastArgs: any[] | null = null;
  let lastThis: any = null;
  let result: any;
  let lastCallTime: number | null = null;
  let lastInvokeTime = 0;
  const leading = options.leading || false;
  const trailing = options.trailing !== false;
  const maxWait = options.maxWait;
  const maxing = maxWait !== undefined;

  function invokeFunc(time: number) {
    const args = lastArgs;
    const thisArg = lastThis;

    lastArgs = lastThis = null;
    lastInvokeTime = time;
    result = func.apply(thisArg, args!);
    return result;
  }

  function leadingEdge(time: number) {
    lastInvokeTime = time;
    timeout = setTimeout(timerExpired, wait);
    return leading ? invokeFunc(time) : result;
  }

  function remainingWait(time: number) {
    const timeSinceLastCall = time - (lastCallTime || 0);
    const timeSinceLastInvoke = time - lastInvokeTime;
    const timeWaiting = wait - timeSinceLastCall;

    return maxing
      ? Math.min(timeWaiting, (maxWait || 0) - timeSinceLastInvoke)
      : timeWaiting;
  }

  function shouldInvoke(time: number) {
    const timeSinceLastCall = time - (lastCallTime || 0);
    const timeSinceLastInvoke = time - lastInvokeTime;

    return (
      lastCallTime === null ||
      timeSinceLastCall >= wait ||
      timeSinceLastCall < 0 ||
      (maxing && timeSinceLastInvoke >= (maxWait || 0))
    );
  }

  function timerExpired() {
    const time = Date.now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    timeout = setTimeout(timerExpired, remainingWait(time));
  }

  function trailingEdge(time: number) {
    timeout = null;

    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = lastThis = null;
    return result;
  }

  function cancel() {
    if (timeout !== null) {
      clearTimeout(timeout);
    }
    lastInvokeTime = 0;
    lastArgs = lastCallTime = lastThis = timeout = null;
  }

  function flush() {
    return timeout === null ? result : trailingEdge(Date.now());
  }

  function debounced(this: any, ...args: any[]) {
    const time = Date.now();
    const isInvoking = shouldInvoke(time);

    lastArgs = args;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timeout === null) {
        return leadingEdge(lastCallTime);
      }
      if (maxing) {
        timeout = setTimeout(timerExpired, wait);
        return invokeFunc(lastCallTime);
      }
    }
    if (timeout === null) {
      timeout = setTimeout(timerExpired, wait);
    }
    return result;
  }

  debounced.cancel = cancel;
  debounced.flush = flush;

  return debounced as T & { cancel: () => void; flush: () => void };
}

/**
 * Creates a throttled version of a function that only invokes func at most once
 * per every wait milliseconds.
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  options: { leading?: boolean; trailing?: boolean } = {}
): T & { cancel: () => void; flush: () => void } {
  const leading = options.leading !== false;
  const trailing = options.trailing !== false;

  return debounce(func, wait, {
    leading,
    maxWait: wait,
    trailing
  });
}

/**
 * Batches multiple function calls into a single execution
 */
export function batchProcessor<T>(
  processor: (items: T[]) => void | Promise<void>,
  options: {
    maxBatchSize?: number;
    maxWaitTime?: number;
    processImmediately?: boolean;
  } = {}
) {
  const {
    maxBatchSize = 100,
    maxWaitTime = 100,
    processImmediately = false
  } = options;

  let batch: T[] = [];
  let timeout: NodeJS.Timeout | null = null;

  const processBatch = async () => {
    if (batch.length === 0) return;

    const itemsToProcess = batch.splice(0, maxBatchSize);
    await processor(itemsToProcess);

    if (batch.length > 0) {
      processBatch();
    }
  };

  const scheduleBatch = () => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      timeout = null;
      processBatch();
    }, maxWaitTime);
  };

  return {
    add: (item: T) => {
      batch.push(item);

      if (processImmediately && batch.length >= maxBatchSize) {
        processBatch();
      } else {
        scheduleBatch();
      }
    },
    flush: () => {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      return processBatch();
    },
    clear: () => {
      batch = [];
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
    },
    size: () => batch.length
  };
}

/**
 * Memoizes a function to cache results based on arguments
 */
export function memoize<T extends (...args: any[]) => any>(
  func: T,
  options: {
    maxSize?: number;
    ttl?: number;
    keyResolver?: (...args: any[]) => string;
  } = {}
): T {
  const { maxSize = 100, ttl, keyResolver = JSON.stringify } = options;
  const cache = new Map<string, { value: any; timestamp?: number }>();

  return ((...args: any[]) => {
    const key = keyResolver(...args);
    const cached = cache.get(key);

    if (cached) {
      if (!ttl || Date.now() - (cached.timestamp || 0) < ttl) {
        return cached.value;
      }
    }

    const result = func(...args);
    cache.set(key, { value: result, timestamp: Date.now() });

    // Evict oldest entries if cache is too large
    if (cache.size > maxSize) {
      const firstKey = cache.keys().next().value;
      cache.delete(firstKey);
    }

    return result;
  }) as T;
}

/**
 * Request idle callback polyfill for better performance
 */
export const requestIdleCallbackPolyfill = (
  callback: IdleRequestCallback,
  options?: IdleRequestOptions
): number => {
  if ('requestIdleCallback' in window) {
    return window.requestIdleCallback(callback, options);
  }

  // Fallback using setTimeout
  const start = Date.now();
  return window.setTimeout(() => {
    callback({
      didTimeout: false,
      timeRemaining: () => Math.max(0, 50 - (Date.now() - start))
    });
  }, 1) as unknown as number;
};

/**
 * Cancel idle callback polyfill
 */
export const cancelIdleCallbackPolyfill = (handle: number): void => {
  if ('cancelIdleCallback' in window) {
    window.cancelIdleCallback(handle);
  } else {
    window.clearTimeout(handle);
  }
};

/**
 * Performs work in chunks during idle time
 */
export function idleChunkedProcessor<T>(
  items: T[],
  processor: (item: T) => void,
  options: {
    chunkSize?: number;
    onProgress?: (processed: number, total: number) => void;
    onComplete?: () => void;
  } = {}
) {
  const { chunkSize = 10, onProgress, onComplete } = options;
  let currentIndex = 0;
  const totalItems = items.length;

  const processChunk = (deadline: IdleDeadline) => {
    while (
      currentIndex < totalItems &&
      (deadline.timeRemaining() > 0 || deadline.didTimeout)
    ) {
      const endIndex = Math.min(currentIndex + chunkSize, totalItems);
      
      for (let i = currentIndex; i < endIndex; i++) {
        processor(items[i]);
      }
      
      currentIndex = endIndex;
      
      if (onProgress) {
        onProgress(currentIndex, totalItems);
      }
      
      if (currentIndex >= totalItems) {
        if (onComplete) {
          onComplete();
        }
        return;
      }
    }

    // Schedule next chunk
    requestIdleCallbackPolyfill(processChunk);
  };

  const handle = requestIdleCallbackPolyfill(processChunk, { timeout: 1000 });

  return {
    cancel: () => {
      cancelIdleCallbackPolyfill(handle);
    }
  };
}